import { useCallback } from "react";
import {
  WSActions,
  WSAlert,
  WSCard,
  WSControl,
  WSControlGroup,
  WSElement,
  WSFlexBox,
  WSForm,
  WSLoader,
  WSSectionToolbar,
  WSText,
  WSButton,
  WSTooltipIcon,
  useWSSnackbar
} from "@wingspanhq/fe-component-library";
import { useQueryWorkLogConfig } from "../../../../../query/workLogConfig/queries/useQueryWorkLogConfig";
import { useMutationUpdateWorkLogConfig } from "../../../../../query/workLogConfig/mutations/useMutationUpdateWorkLogConfig";
import { WSErrorMessage } from "../../../../../components/WSErrorMessage/WSErrorMessage";
import { IWorkLogConfig } from "../../../../../services/workLogConfig";

type FormData = {
  allowPayeeCreatedWorkLogs: string;
  autoApproveWorkLogs: boolean;
};

const CONTRACTOR_SUBMISSIONS_OPTIONS = [
  {
    value: "disallow",
    label: "Do not allow contractor submissions",
    helperText: "Only you and your team can create work logs for contractors"
  },
  {
    value: "allow",
    label: "Allow contractor submissions",
    helperText: "Work logs are created by contractors"
  }
];

export const RouteWorkLogs: React.FC = () => {
  const { openSnackbar } = useWSSnackbar();
  const queryWorkLogConfig = useQueryWorkLogConfig();
  const [updateConfig, updateConfigMeta] = useMutationUpdateWorkLogConfig();

  const handleSubmit = useCallback(
    async (formData: FormData) => {
      await updateConfig(
        {
          allowPayeeCreatedWorkLogs: {
            enabled: formData.allowPayeeCreatedWorkLogs === "allow"
          },
          autoApproveWorkLogs: {
            enabled: formData.autoApproveWorkLogs
          }
        },
        {
          onSuccess: () => {
            openSnackbar({
              type: "success",
              message: "Work log settings saved"
            });
          }
        }
      );
    },
    [updateConfig, openSnackbar]
  );

  const is404 = (queryWorkLogConfig.error as any)?.response?.status === 404;
  const isLoading = queryWorkLogConfig.isLoading;
  const isNonRecoverableError = queryWorkLogConfig.isError && !is404;

  const config: IWorkLogConfig | undefined =
    queryWorkLogConfig.data ??
    (is404
      ? {
          allowPayeeCreatedWorkLogs: { enabled: false },
          autoApproveWorkLogs: { enabled: false }
        }
      : undefined);

  if (isLoading) {
    return (
      <WSElement my="5XL">
        <WSLoader.Spinner />
      </WSElement>
    );
  }

  if (isNonRecoverableError || !config) {
    return (
      <WSErrorMessage error={queryWorkLogConfig.error} contextKey="WorkLog" />
    );
  }

  return (
    <WSElement colorBackground="gray50" style={{ maxWidth: 912 }}>
      <WSForm<FormData>
        onSubmit={handleSubmit}
        defaultValues={{
          allowPayeeCreatedWorkLogs: config.allowPayeeCreatedWorkLogs?.enabled
            ? "allow"
            : "disallow",
          autoApproveWorkLogs: config.autoApproveWorkLogs?.enabled ?? false
        }}
      >
        <WSCard mb="2XL">
          <WSSectionToolbar title="Work logs settings" mb="2XL" />
          <WSAlert
            theme="info"
            icon="info-circle"
            title="About work log settings"
            mb="2XL"
          >
            These settings control how contractors submit time and expenses for
            approval (work log engagements only).
          </WSAlert>

          <WSForm.Field
            name="allowPayeeCreatedWorkLogs"
            component={WSControlGroup}
            componentProps={{
              type: "radio",
              chip: true,
              options: CONTRACTOR_SUBMISSIONS_OPTIONS
            }}
            mb="2XL"
          />

          <WSElement colorBackground="gray50" p="XL">
            <WSSectionToolbar title="Auto-approval" mb="M" />

            <WSFlexBox.CenterY>
              <WSForm.Field
                name="autoApproveWorkLogs"
                component={WSControl}
                componentProps={{
                  type: "switch",
                  label: "Auto-approve work logs"
                }}
              />
              <WSTooltipIcon
                tooltip="When enabled, work logs will be automatically approved when submitted, bypassing the review step."
                name="info-circle"
                size="S"
                ml="XS"
              />
            </WSFlexBox.CenterY>

            <WSFlexBox alignItems="center" mt="XL">
              <WSText.ParagraphSm color="gray500">
                Learn more about
              </WSText.ParagraphSm>
              <WSButton.Link
                ml="XS"
                size="S"
                onClick={() =>
                  window.open(
                    "https://help.wingspan.app/work-logs",
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
                rightIcon="open-tab"
                type="button"
              >
                work logs
              </WSButton.Link>
            </WSFlexBox>
          </WSElement>
        </WSCard>

        <WSErrorMessage error={updateConfigMeta.error} contextKey="WorkLog" />

        <WSActions
          alignment="fill"
          buttons={[
            {
              label: "Save",
              kind: "Primary",
              type: "submit",
              loading: updateConfigMeta.isLoading
            }
          ]}
        />

        <WSText.ParagraphXs color="gray400" mt="XL">
          Work logs settings can be viewed and updated at any time in your
          settings.
        </WSText.ParagraphXs>
      </WSForm>
    </WSElement>
  );
};
